import type React from 'react'
import type { ITextBoxProps } from 'atoms/TextBox'
import type {
  IMCDescriptionWithMore,
  IMCTitleWithLabels,
} from 'blocks/MultiColumns/Templates'
import type { IDescriptionProps } from '../../Description'

import {
  MCDescriptionWithMore,
  MCTitleWithLabels,
} from 'blocks/MultiColumns/Templates'
import { usePropForDesktopEmptyString } from 'utils/hooks'
import { Description } from '../../Description'
import { DescriptionRowGap } from '../../Description.h'
import { BannerDescriptionSC } from './DetailsPageDescriptionType1.styled'

export interface IDetailsPageDescriptionType1 extends Omit<
  IMCTitleWithLabels & IDescriptionProps & IMCDescriptionWithMore,
  'children' | 'rowGap' | 'title'
> {
  title: ITextBoxProps['title']
  rowGap?: DescriptionRowGap
  children?: React.ReactNode
  description?: string
  onClickMore?: (args: any) => void
  dataTestIdTitle?: string
  /** Renders the block as the details page `banner-description` overlay. */
  bannerDescription?: boolean
  /**
   * Set to `false` when the description, directors and stars are rendered by
   * the details area that follows the banner instead of by this block.
   */
  withDescription?: boolean
}

export const DetailsPageDescriptionType1 = ({
  labels,
  contentLabels,
  statusBadge,
  statusBadgeTone,
  statusBadgeFirst,
  ageRatingLabel,
  paid,
  rented,
  purchaseTitle,
  title = '',
  titleMaxLines,
  subtitle,
  rowGap = DescriptionRowGap.INDENT_20,
  children,
  withUsingPortal,
  description = '',
  directors,
  stars,
  width = 100,
  onClickMore,
  dataTestIdTitle,
  quality,
  dolbyAtmos,
  bannerDescription = false,
  withDescription = true,
  verticalVideo,
  ...rest
}: IDetailsPageDescriptionType1) => {
  const Root = bannerDescription ? BannerDescriptionSC : Description
  const textDirectors = usePropForDesktopEmptyString(directors)
  const textStars = usePropForDesktopEmptyString(stars)

  return (
    <Root rowGap={rowGap} width={width} {...rest}>
      <MCTitleWithLabels
        title={title}
        titleMaxLines={titleMaxLines}
        subtitle={subtitle}
        width={width}
        columnValue={bannerDescription ? 1 : 2}
        labels={labels}
        contentLabels={contentLabels}
        statusBadge={statusBadge}
        statusBadgeTone={statusBadgeTone}
        statusBadgeFirst={statusBadgeFirst}
        ageRatingLabel={ageRatingLabel}
        paid={paid}
        rented={rented}
        purchaseTitle={purchaseTitle}
        quality={quality}
        dolbyAtmos={dolbyAtmos}
        verticalVideo={verticalVideo}
        dataTestId={dataTestIdTitle}
      />
      {withDescription && (
        <MCDescriptionWithMore
          text={description}
          labels={labels}
          more={rest.more}
          episodePopup={rest.episodePopup}
          titlePopup={rest.titlePopup}
          labelsPopup={rest.labelsPopup}
          listPopup={rest.listPopup}
          titleButton={rest.titleButton}
          directorsPopup={directors}
          starsPopup={stars}
          fullInformation={rest.fullInformation}
          textDirectors={textDirectors}
          textStars={textStars}
          width={width}
          popupWidth={rest.popupWidth}
          withUsingPortal={withUsingPortal}
          directors={directors}
          stars={stars}
          maxLineMore={rest.maxLineMore}
          onClickMore={onClickMore}
          directorsTitle={rest.directorsTitle}
          starsTitle={rest.starsTitle}
        />
      )}
      {children}
    </Root>
  )
}
